import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/lib/api";

function formatDate(d) {
  try {
    const dt = typeof d === "string" ? new Date(d) : d;
    return dt.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

export default function Guestbook() {
  const [entries, setEntries] = useState([]);
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/guestbook`);
      setEntries(data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !message.trim()) {
      toast.error("Please add your name and a short message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/guestbook`, { name, message });
      setName("");
      setMessage("");
      toast.success("Thank you for the blessings.");
      await load();
    } catch (err) {
      console.error(err);
      toast.error("Could not post. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="guestbook"
      data-testid="guestbook-section"
      className="border-t border-[#EFE6D2]"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-12 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-16">
        {/* Form */}
        <div className="lg:col-span-5">
          <p className="overline">
            <span className="divider-line mr-4" />
            Guestbook
          </p>
          <h2 className="font-serif-wb mt-6 text-4xl sm:text-5xl tracking-tight">
            Leave us a <span className="italic" style={{ color: "var(--wb-primary)" }}>blessing.</span>
          </h2>
          <p className="mt-5 text-[#5C5C5C] leading-relaxed max-w-md">
            Words to carry with us into this next chapter — we'll read them
            all, together.
          </p>

          <form onSubmit={submit} className="mt-10 space-y-6" data-testid="guestbook-form">
            <div>
              <label className="overline block mb-2">Your name</label>
              <input
                className="wb-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="guestbook-input-name"
                placeholder="e.g. Aarti & Family"
                required
              />
            </div>
            <div>
              <label className="overline block mb-2">Message</label>
              <textarea
                className="wb-input"
                rows={4}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                data-testid="guestbook-input-message"
                placeholder="Write a short blessing or memory…"
                required
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              data-testid="guestbook-submit-button"
              className="wb-btn"
            >
              {submitting ? "Sending…" : "Post Message"}
            </button>
          </form>
        </div>

        {/* Entries */}
        <div className="lg:col-span-7" data-testid="guestbook-list">
          <p className="overline mb-8">
            <span className="divider-line mr-4" />
            From our well-wishers
          </p>

          {loading ? (
            <p className="text-[#5C5C5C] text-sm">Loading messages…</p>
          ) : entries.length === 0 ? (
            <div className="border border-dashed border-[#EFE6D2] p-10 text-center">
              <p className="font-serif-wb italic text-2xl text-[#5C5C5C]">
                Be the first to leave a blessing.
              </p>
            </div>
          ) : (
            <div className="space-y-8 max-h-[640px] overflow-y-auto pr-2">
              {entries.map((g, i) => (
                <motion.article
                  key={g.id || i}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: i * 0.04 }}
                  className="border-b border-[#EFE6D2] pb-8"
                  data-testid={`guestbook-entry-${i}`}
                >
                  <p className="font-serif-wb text-xl sm:text-2xl text-[#1A1A1A] leading-snug">
                    "{g.message}"
                  </p>
                  <div className="mt-4 flex items-center gap-3">
                    <span
                      className="inline-block w-8 h-px"
                      style={{ background: "var(--wb-primary)" }}
                    />
                    <p className="overline">{g.name}</p>
                    <span className="text-xs text-[#a39a89]">
                      · {formatDate(g.created_at)}
                    </span>
                  </div>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
